import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, CheckCircle, Clock, Warning, CurrencyEur, MagnifyingGlass, ArrowRight, EnvelopeSimple, FilePdf, PencilSimple, PaperPlaneTilt } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PdfViewerModal } from '../invoicing/PdfViewerModal';
import { QuickInvoiceModal } from '../invoicing/QuickInvoiceModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon', color: '#5E5F66', bg: '#F4F5F7' },
  emise: { label: 'Emise', color: '#1565C0', bg: '#E3F2FD' },
  payee: { label: 'Payee', color: '#2E7D32', bg: '#E8F5E9' },
  annulee: { label: 'Annulee', color: '#C62828', bg: '#FFEBEE' },
  accepte: { label: 'Accepte', color: '#2E7D32', bg: '#E8F5E9' },
  refuse: { label: 'Refuse', color: '#C62828', bg: '#FFEBEE' },
};

const INVOICE_STATUSES = ['brouillon', 'emise', 'payee', 'annulee'];
const QUOTE_STATUSES = ['brouillon', 'emise', 'accepte', 'refuse'];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.brouillon;
  return <span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>{cfg.label}</span>;
};

const isOverdue = (item) => {
  if (item.status !== 'emise' || !item.due_date) return false;
  return new Date(item.due_date) < new Date();
};

export const BillingTrackingWidget = ({ wrapStyle }) => {
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSub, setActiveSub] = useState('factures');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchLocal, setSearchLocal] = useState('');
  const [editingDue, setEditingDue] = useState({ id: null, value: '' });
  const [pdfViewer, setPdfViewer] = useState({ show: false, url: '', title: '' });
  const [editItem, setEditItem] = useState({ show: false, type: 'facture', item: null });

  const fetchData = useCallback(async () => {
    try {
      const [invRes, qRes] = await Promise.all([
        axios.get(`${API_URL}/api/invoicing`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/quotes`, { withCredentials: true }),
      ]);
      setInvoices(invRes.data);
      setQuotes(qRes.data);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, newStatus, type) => {
    try {
      const url = type === 'devis' ? `${API_URL}/api/invoicing/quotes/${id}` : `${API_URL}/api/invoicing/${id}`;
      await axios.put(url, { status: newStatus }, { withCredentials: true });
      toast.success('Statut mis a jour');
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const saveDueDate = async (id, value) => {
    try {
      await axios.put(`${API_URL}/api/invoicing/${id}`, { due_date: value }, { withCredentials: true });
      toast.success('Echeance mise a jour');
      setEditingDue({ id: null, value: '' });
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const openPdf = async (item, type) => {
    const url = type === 'devis' ? `${API_URL}/api/invoicing/quotes/${item.id}/export/pdf` : `${API_URL}/api/invoicing/${item.id}/export/pdf`;
    try {
      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfViewer({ show: true, url: blobUrl, title: `${type === 'devis' ? 'Devis' : 'Facture'} ${item.number}` });
    } catch { toast.error('Impossible de charger le PDF'); }
  };

  const sendInvoiceToClient = async (item) => {
    if (!item.client_email) {
      toast.error('Saisir email client d\'abord');
      return;
    }
    if (!window.confirm(`Envoyer la facture ${item.number} a ${item.client_email} ?`)) return;
    try {
      const res = await axios.post(`${API_URL}/api/invoicing/${item.id}/send-to-client`, {}, { withCredentials: true });
      toast.success(`Facture envoyee a ${res.data.to}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    }
  };

  const closePdf = () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ show: false, url: '', title: '' });
  };

  const sendReminder = (item) => {
    if (!item.client_email) {
      toast.error('Aucun email client renseigne');
      return;
    }
    const subject = encodeURIComponent(`Relance facture ${item.number}`);
    const body = encodeURIComponent(
      `Bonjour ${item.client_name || ''},\n\n` +
      `Sauf erreur de notre part, nous n'avons pas recu le reglement de la facture N ${item.number} ` +
      `d'un montant de ${(item.total_ttc || 0).toLocaleString('fr-FR')} EUR TTC, ` +
      `echue le ${item.due_date || '-'}.\n\n` +
      `Nous vous remercions de bien vouloir proceder au reglement dans les meilleurs delais.\n\n` +
      `Cordialement,\nBATI-BOYS`
    );
    window.location.href = `mailto:${item.client_email}?subject=${subject}&body=${body}`;
  };

  const kpis = useMemo(() => {
    const paid = invoices.filter(i => i.status === 'payee').reduce((s, i) => s + (i.total_ttc || 0), 0);
    const pending = invoices.filter(i => i.status === 'emise' && !isOverdue(i)).reduce((s, i) => s + (i.total_ttc || 0), 0);
    const overdue = invoices.filter(i => isOverdue(i)).reduce((s, i) => s + (i.total_ttc || 0), 0);
    const draft = invoices.filter(i => i.status === 'brouillon').reduce((s, i) => s + (i.total_ttc || 0), 0);
    const quoteAccepted = quotes.filter(q => q.status === 'accepte').reduce((s, q) => s + (q.total_ttc || 0), 0);
    const quoteSent = quotes.filter(q => q.status === 'emise').reduce((s, q) => s + (q.total_ttc || 0), 0);
    return { paid, pending, overdue, draft, quoteAccepted, quoteSent, countPaid: invoices.filter(i => i.status === 'payee').length, countOverdue: invoices.filter(i => isOverdue(i)).length };
  }, [invoices, quotes]);

  const filtered = useMemo(() => {
    const base = activeSub === 'factures' ? invoices : quotes;
    return base.filter(item => {
      if (statusFilter === 'overdue') return isOverdue(item);
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (!searchLocal) return true;
      const s = searchLocal.toLowerCase();
      return (item.number || '').toLowerCase().includes(s) ||
        (item.client_name || '').toLowerCase().includes(s) ||
        (item.project_name || '').toLowerCase().includes(s);
    });
  }, [activeSub, invoices, quotes, statusFilter, searchLocal]);

  if (loading) return null;

  const kpiCard = (label, value, color, icon, count) => (
    <div className="stat-card" style={{ minWidth: 0 }}>
      <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem' }}>{icon} {label}</div>
      <div className="stat-value" style={{ fontSize: '1.3rem', color }}>{value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}&euro;</div>
      {count !== undefined && <div style={{ fontSize: '0.7rem', color: '#5E5F66' }}>{count} doc(s)</div>}
    </div>
  );

  const currentStatuses = activeSub === 'factures' ? INVOICE_STATUSES : QUOTE_STATUSES;

  return (
    <div style={wrapStyle}>
      <div className="card" data-testid="billing-tracking-widget">
        <div className="card-header" style={{ backgroundColor: '#FFF4E5' }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#EF6C00' }}>
            <Receipt size={20} weight="bold" /> Suivi Facturation & Devis
          </h3>
        </div>
        <div style={{ padding: '20px' }}>
          {/* KPIs */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {kpiCard('Encaisse', kpis.paid, '#2E7D32', <CheckCircle size={14} />, kpis.countPaid)}
            {kpiCard('En attente', kpis.pending, '#1565C0', <Clock size={14} />)}
            {kpiCard('En retard', kpis.overdue, '#C62828', <Warning size={14} />, kpis.countOverdue)}
            {kpiCard('Brouillons', kpis.draft, '#5E5F66', <CurrencyEur size={14} />)}
            {kpiCard('Devis acceptes', kpis.quoteAccepted, '#2E7D32', <CheckCircle size={14} />)}
            {kpiCard('Devis emis', kpis.quoteSent, '#1565C0', <ArrowRight size={14} />)}
          </div>

          {/* Sub tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E2E2E6', marginBottom: '14px' }}>
            {[{ key: 'factures', label: `Factures (${invoices.length})` }, { key: 'devis', label: `Devis (${quotes.length})` }].map(tab => (
              <button key={tab.key} onClick={() => { setActiveSub(tab.key); setStatusFilter('all'); }} data-testid={`billing-sub-${tab.key}`}
                style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', backgroundColor: 'transparent', borderBottom: activeSub === tab.key ? '2px solid #EF6C00' : '2px solid transparent', color: activeSub === tab.key ? '#EF6C00' : '#5E5F66', marginBottom: '-2px' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '180px' }}>
              <MagnifyingGlass size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9E9EA7', pointerEvents: 'none' }} />
              <input type="text" className="form-input" placeholder="Recherche (n, client, chantier)" value={searchLocal} onChange={(e) => setSearchLocal(e.target.value)}
                data-testid="billing-search" style={{ paddingLeft: '36px', fontSize: '0.85rem' }} />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger style={{ width: '200px' }} data-testid="billing-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="all">Tous les statuts</SelectItem>
                {activeSub === 'factures' && <SelectItem value="overdue">En retard</SelectItem>}
                {currentStatuses.map(s => (
                  <SelectItem key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" data-testid="billing-tracking-table" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th>N {activeSub === 'factures' ? 'Facture' : 'Devis'}</th>
                  <th>Client</th>
                  <th>Date</th>
                  {activeSub === 'factures' && <th>Echeance</th>}
                  <th style={{ textAlign: 'right' }}>HT</th>
                  <th style={{ textAlign: 'right' }}>TVA</th>
                  <th style={{ textAlign: 'right' }}>TTC</th>
                  <th>Email</th>
                  <th>Tel</th>
                  <th>Statut</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={activeSub === 'factures' ? 11 : 10} style={{ textAlign: 'center', padding: '24px', color: '#9E9EA7' }}>Aucun resultat</td></tr>
                ) : filtered.map(item => {
                  const overdue = isOverdue(item);
                  return (
                    <tr key={item.id} style={overdue ? { backgroundColor: '#FFF5F5' } : {}}>
                      <td style={{ fontWeight: '700', color: '#002FA7' }}>{item.number}</td>
                      <td>{item.client_name || <span style={{ color: '#9E9EA7', fontStyle: 'italic' }}>-</span>}</td>
                      <td>{item.date}</td>
                      {activeSub === 'factures' && (
                        <td style={{ color: overdue ? '#C62828' : 'inherit', fontWeight: overdue ? '600' : '400' }}>
                          {editingDue.id === item.id ? (
                            <input type="date" className="form-input"
                              style={{ padding: '2px 4px', fontSize: '0.75rem', height: '26px' }}
                              value={editingDue.value}
                              onChange={(e) => setEditingDue({ id: item.id, value: e.target.value })}
                              onBlur={() => editingDue.value && editingDue.value !== item.due_date ? saveDueDate(item.id, editingDue.value) : setEditingDue({ id: null, value: '' })}
                              onKeyDown={(e) => { if (e.key === 'Enter' && editingDue.value) saveDueDate(item.id, editingDue.value); if (e.key === 'Escape') setEditingDue({ id: null, value: '' }); }}
                              autoFocus
                              data-testid={`due-date-input-${item.id}`}
                            />
                          ) : (
                            <button
                              onClick={() => setEditingDue({ id: item.id, value: item.due_date || '' })}
                              title="Cliquer pour modifier"
                              data-testid={`due-date-${item.id}`}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 'inherit', padding: 0, textAlign: 'left', fontSize: '0.8rem' }}
                            >
                              {item.due_date || <span style={{ color: '#9E9EA7', fontStyle: 'italic' }}>Saisir</span>}
                            </button>
                          )}
                        </td>
                      )}
                      <td style={{ textAlign: 'right', fontWeight: '600' }}>{(item.total_ht || 0).toLocaleString('fr-FR')} &euro;</td>
                      <td style={{ textAlign: 'right' }}>{(item.total_tva || 0).toLocaleString('fr-FR')} &euro;</td>
                      <td style={{ textAlign: 'right', fontWeight: '700', color: '#2E7D32' }}>{(item.total_ttc || 0).toLocaleString('fr-FR')} &euro;</td>
                      <td style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{item.client_email || '-'}</td>
                      <td style={{ fontSize: '0.75rem', color: '#5E5F66' }}>{item.client_phone || '-'}</td>
                      <td><StatusBadge status={item.status} /></td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <Select value={item.status} onValueChange={(v) => updateStatus(item.id, v, activeSub === 'factures' ? 'facture' : 'devis')}>
                            <SelectTrigger style={{ width: '110px', height: '28px', fontSize: '0.75rem' }} data-testid={`change-status-${item.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="z-[9999]">
                              {currentStatuses.map(s => (
                                <SelectItem key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button onClick={() => setEditItem({ show: true, type: activeSub === 'factures' ? 'facture' : 'devis', item })} className="btn btn-secondary btn-sm" title="Modifier" data-testid={`edit-${item.id}`} style={{ padding: '4px 8px' }}>
                            <PencilSimple size={14} />
                          </button>
                          <button onClick={() => openPdf(item, activeSub === 'factures' ? 'facture' : 'devis')} className="btn btn-secondary btn-sm" title="Voir PDF" data-testid={`view-pdf-${item.id}`} style={{ padding: '4px 8px' }}>
                            <FilePdf size={14} />
                          </button>
                          {activeSub === 'factures' && (
                            <button
                              onClick={() => sendInvoiceToClient(item)}
                              className="btn btn-secondary btn-sm"
                              title={item.client_email ? `Envoyer au client (${item.client_email})` : 'Aucun email client'}
                              disabled={!item.client_email}
                              data-testid={`send-to-client-${item.id}`}
                              style={{ padding: '4px 8px', opacity: item.client_email ? 1 : 0.4, cursor: item.client_email ? 'pointer' : 'not-allowed' }}
                            >
                              <PaperPlaneTilt size={14} />
                            </button>
                          )}
                          {activeSub === 'factures' && overdue && (
                            <button onClick={() => sendReminder(item)} className="btn btn-secondary btn-sm" title="Relancer par email" data-testid={`remind-${item.id}`} style={{ padding: '4px 8px' }}>
                              <EnvelopeSimple size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <PdfViewerModal show={pdfViewer.show} url={pdfViewer.url} title={pdfViewer.title} onClose={closePdf} />
      <QuickInvoiceModal
        show={editItem.show}
        type={editItem.type}
        item={editItem.item}
        onClose={() => setEditItem({ show: false, type: 'facture', item: null })}
        onCreated={() => { fetchData(); setEditItem({ show: false, type: 'facture', item: null }); }}
      />
    </div>
  );
};
